import React, { useCallback, useMemo, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { format, isEqual, parseISO } from "date-fns";

import {
  TaskComment as TaskCommentType,
  TaskCommentWithAuthorRoles,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  AttachmentId,
  TaskCommentId,
} from "@fartherfinance/frontend/api/Types";

import TaskAttachment from "../../../TaskAttachment/TaskAttachment";
import { useTaskPermissions } from "../../../taskPermissionsContext";
import { formatRelativeTimestamp, getPersonNameV2 } from "../utils";
import Avatar from "@src/multiCustodian/components/Avatar/Avatar";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import AdvisorTag from "@src/multiCustodian/pages/Advisor/Tasks/TaskDetails/Components/AdvisorTag/AdvisorTag";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Menu from "@src/sharedComponents/Menu/Menu";
import Textarea from "@src/sharedComponents/Textarea/Textarea";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TaskComment.module.css";

interface TaskCommentProps {
  comment: TaskCommentType | TaskCommentWithAuthorRoles;
  onCommentDelete: (commentId: TaskCommentId) => Promise<void>;
  onCommentUpdate: (commentId: TaskCommentId, content: string) => Promise<void>;
  onAttachmentDelete: (attachmentId: AttachmentId) => Promise<void>;
  isAdvisor?: boolean;
}

const TaskComment: React.FC<TaskCommentProps> = ({
  comment,
  onCommentDelete,
  onCommentUpdate,
  onAttachmentDelete,
  isAdvisor,
}) => {
  const [commentValue, setCommentValue] = useState(comment.content);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [attachmentIdsLoading, setAttachmentIdsLoading] = useState<
    AttachmentId[]
  >([]);
  const { commentPermissions, attachmentPermissions } = useTaskPermissions();

  const commentTimestamps = useMemo(() => {
    return {
      createdTs: parseISO(comment.createdTs),
      updatedTs: parseISO(comment.updatedTs),
    };
  }, [comment]);

  const isCommentEdited = !isEqual(
    commentTimestamps.createdTs,
    commentTimestamps.updatedTs
  );
  const authorAvatarSource = comment.author.headshotPath ?? undefined;
  const authorInitials = comment.author.name
    ? `${comment.author.name.first[0]}${comment.author.name.last[0]}`
    : undefined;

  const handleMouseEnter = (): void => {
    setIsHovering(true);
  };

  const handleMouseLeave = (): void => {
    setIsHovering(false);
  };

  const handleCommentUpdate = (newCommentValue: string): void => {
    if (newCommentValue !== comment.content) {
      onCommentUpdate(comment.id, newCommentValue);
    }
    setIsEditing(false);
  };

  const handleCommentDelete = useCallback((): void => {
    onCommentDelete(comment.id);
  }, [comment.id, onCommentDelete]);

  const handleDeleteAttachment = async (
    attachmentId: AttachmentId
  ): Promise<void> => {
    try {
      setAttachmentIdsLoading((attachmentIds) =>
        attachmentIds.concat(attachmentId)
      );
      await onAttachmentDelete(attachmentId);
    } finally {
      setAttachmentIdsLoading((attachmentIds) =>
        attachmentIds.filter((id) => id !== attachmentId)
      );
    }
  };

  const handleCommentValue = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setCommentValue(event.target.value);
  };

  const commentMenuOptions = useMemo(() => {
    return [
      ...(commentPermissions(comment).isAllowedToUpdate
        ? [
            {
              label: "Edit",
              onClick: () => setIsEditing(true),
              isCloseOnClick: true,
            },
          ]
        : []),
      ...(commentPermissions(comment).isAllowedToDelete
        ? [
            {
              label: "Delete",
              isDisabled: !commentPermissions(comment).isAllowedToDelete,
              className: styles.delete,
              onClick: handleCommentDelete,
            },
          ]
        : []),
    ];
  }, [comment, handleCommentDelete, commentPermissions]);

  return (
    <div
      className={toClassName(styles.container, {
        [styles.containerEditing]: isEditing,
      })}
    >
      <div className={styles.header}>
        <Avatar
          className={styles.avatar}
          imageSource={authorAvatarSource}
          initials={authorInitials}
        />

        <div className={styles.metadata}>
          <Text variant="body">
            {getPersonNameV2(comment.author)}
            {isAdvisor && <AdvisorTag />}
          </Text>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="2"
            height="2"
            viewBox="0 0 2 2"
            fill="none"
            className={styles.dot}
          >
            <circle cx="1" cy="1" r="1" fill="currentColor" />
          </svg>
          <Text
            variant="body"
            color="textSubtle"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isHovering
              ? format(commentTimestamps.createdTs, "M/d/yy 'at' h:mm a")
              : formatRelativeTimestamp(commentTimestamps.createdTs)}
            &nbsp;
            {isCommentEdited && <>(edited)</>}
          </Text>
        </div>

        {commentMenuOptions.length > 0 && (
          <Menu options={commentMenuOptions}>
            {({ handleOpenMenu }) => (
              <IconButton
                size="small"
                className={styles.menuButton}
                iconClassName={styles.menuIcon}
                onClick={handleOpenMenu}
                IconComponent={MoreHorizIcon}
              />
            )}
          </Menu>
        )}
      </div>
      <div className={styles.content}>
        {isEditing ? (
          <>
            <Textarea
              value={commentValue}
              onChange={handleCommentValue}
              maxRows={10}
            />
            <div className={styles.updateActions}>
              <Button
                variant="text"
                buttonType="primary"
                text="Cancel"
                onClick={() => {
                  setIsEditing(false);
                  setCommentValue(comment.content);
                }}
              />
              <Button
                variant="contained"
                buttonType="primary"
                text="Save"
                onClick={() => handleCommentUpdate(commentValue)}
              />
            </div>
          </>
        ) : (
          <>
            <Text className={styles.comment} variant="body" color="textSubtle">
              {commentValue}
            </Text>
            {comment.attachments.length > 0 && (
              <div className={styles.commentAttachments}>
                {comment.attachments.map((attachment) => (
                  <TaskAttachment
                    key={attachment.id}
                    attachment={attachment}
                    isLoading={attachmentIdsLoading.includes(attachment.id)}
                    isAllowedToDelete={
                      attachmentPermissions(attachment).isAllowedToDelete
                    }
                    onRemove={() => handleDeleteAttachment(attachment.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskComment;
